import { useValidatedCubeQuery } from "app/hooks/cube";
import { createCombinedError } from "app/hooks/cube/utils";

import { getCostFieldName } from "./filterTransformUtils";
import { useCostReportingConfig } from "./useCostReportingConfig";
import { costStatsRawDataSchema } from "./useCostStats.schema";

import type { Query, Filter, ResultSet } from "@cubejs-client/core";
import type { CloudCostType } from "app/contexts/UserContext";
import type { Range } from "types/cube";

const CUBE_NAME = "CSO" as const;

interface UseCostStatsParams {
  dashboardKey: string;
  dateRange: Range;
  sectionKey?: string;
  enabled?: boolean;
}

interface ChartDataPoint {
  x: string;
  y: number;
}

interface ChartSeries {
  id: string;
  data: ChartDataPoint[];
}

interface UseCostStatsResult {
  monthlySavingOpportunities: ChartSeries[];
  nonCompliantResourceCounts: ChartSeries[];
  isLoading: boolean;
  error: Error | null;
}

function buildQuery(
  dashboardKey: string,
  dateRange: Range,
  costType: CloudCostType | undefined,
  sectionKey?: string,
): Query {
  const costFieldName = getCostFieldName(costType, CUBE_NAME);

  return {
    measures: [costFieldName, `${CUBE_NAME}.nonCompliantResourceCount`],
    segments: sectionKey ? [] : [`${CUBE_NAME}.summary`],
    timeDimensions: [
      {
        dimension: `${CUBE_NAME}.date`,
        granularity: "day",
        dateRange,
      },
    ],
    filters: [
      {
        member: `${CUBE_NAME}.dashboard_key`,
        operator: "equals" as const,
        values: [dashboardKey],
      },
      ...(sectionKey
        ? [
            {
              member: `${CUBE_NAME}.section_key`,
              operator: "equals" as const,
              values: [sectionKey],
            } as Filter,
          ]
        : []),
    ],
    order: {
      [`${CUBE_NAME}.date`]: "desc",
    },
  };
}

function formatResults(
  key: string,
  resultSet: ResultSet | null,
): ChartSeries[] {
  return (resultSet?.series() ?? [])
    .filter((s) => s.key === key)
    .map((s) => ({
      id: s.key,
      data: s.series.map((r) => ({ x: r.x, y: r.value })),
    }));
}

export function useCostStats({
  dashboardKey,
  dateRange,
  sectionKey,
  enabled = true,
}: UseCostStatsParams): UseCostStatsResult {
  const costType = useCostReportingConfig();
  const costFieldName = getCostFieldName(costType, CUBE_NAME);

  const { resultSet, isLoading, error, validationError } =
    useValidatedCubeQuery(
      buildQuery(dashboardKey, dateRange, costType, sectionKey),
      costStatsRawDataSchema,
      { skip: !enabled },
    );

  const combinedError = createCombinedError(
    { error, validationError },
    { errorPrefix: "Stats query validation failed" },
  );

  const monthlySavingOpportunities = formatResults(costFieldName, resultSet);

  const nonCompliantResourceCounts = formatResults(
    `${CUBE_NAME}.nonCompliantResourceCount`,
    resultSet,
  );

  return {
    monthlySavingOpportunities,
    nonCompliantResourceCounts,
    isLoading,
    error: combinedError,
  };
}
